import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import Group from '../../../../../components/form/elements/group';
import { useEffect, useMemo } from 'react';
import {
  amount,
  makeButton,
  product,
  renderWarehousePartyNumber,
  warehouse,
} from '../form-parts';
import { useService } from '../../../../../services/services';
import { useParams } from 'react-router-dom';
import Select from '../../../../../components/form/elements/select';
import Decimal from '../../../../../components/form/elements/decimal';
import Watch from '../../../../../components/form/elements/watch';
import ObtainTypes from '../../../../../enums/obtain-types';
import { handlePrice, handleSum } from '../use-deal-form-calculations';
import { useWarehouseContext } from '../warehouse-context';
import DealType from '../../../../../enums/deal-type';
//import css from '../drawer-form.module.css';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FormComponent } from '../../../../../components/form';
import css from './moved.module.css';

let calculationValues = {
  sum: '',
  price: '',
  vat: '',
  sum_with_vat: '',
  amount: '',
  vat_rate: '',
};

export function useMovedForm(originalFormValues = null, warehouseId = null) {
  const { isOpen, setOpenModal } = useWarehouseContext();
  const { t } = useTranslation();

  const { dealType, farmId } = useParams<{
    dealType: DealType;
    farmId: string;
  }>();

  useEffect(() => {
    if (isOpen) {
      calculationValues = {
        sum: '',
        price: '',
        vat: '',
        sum_with_vat: '',
        amount: '',
        vat_rate: '',
      };
    }
  }, [isOpen]);

  const { dealService, productService, warehouseService } = useService();

  return useMemo<FormComponent[]>(
    () => [
      Group(
        [
          Select(
            'product_obtain_type_id',
            {
              options: [
                {
                  label: t(`classifiers.${ObtainTypes.produced}`),
                  value: ObtainTypes.produced,
                },
                {
                  label: t(`classifiers.${ObtainTypes.purchased}`),
                  value: ObtainTypes.purchased,
                },
                {
                  label: t(`classifiers.${ObtainTypes.recycle_gained}`),
                  value: ObtainTypes.recycle_gained,
                },
              ],
              label: 'pages.warehouse.deals.general_type',
            },
            true
          ),
          ...product(
            t,
            dealService,
            productService,
            farmId,
            dealType,
            false,
            false,
            true,
            '_moved',
            undefined,
            undefined,
            originalFormValues
          ),
          amount(true, calculationValues, {
            enableNewPartyNumber: true,
            dealService,
            farmId,
            dealType,
            originalFormValues,
          }),
        ],
        null,
        null,
        false,
        true,
        `${css['moved-amount']} no-border-bottom`
      ),
      Group(
        [
          renderWarehousePartyNumber(),
          Group(
            [
              warehouse(
                warehouseService,
                farmId,
                false,
                true,
                'to_warehouse_id',
                'to_warehouse'
              ),
              makeButton(
                'pages.warehouse.deals.new_warehouse',
                css['new_warehouse'],
                '',
                faPlus,
                undefined,
                () => setOpenModal(true)
              ),
            ],
            null,
            null,
            false,
            true,
            `${css['new_warehouse_box']} 'no-border-bottom'`
          ),
          Watch(
            Decimal(
              'price',
              'pages.warehouse.deals.price_unit',
              null,
              null,
              true
            ),
            ({ value, parentName, setValue, getValues }) => {
              return handlePrice(
                'price',
                value,
                parentName,
                setValue,
                getValues,
                calculationValues
              );
            }
          ),
          Watch(
            Decimal('sum', 'pages.warehouse.deals.sum', null, null, true),
            ({ value, parentName, setValue, getValues }) => {
              return handleSum(
                'sum',
                value,
                parentName,
                setValue,
                getValues,
                calculationValues
              );
            }
          ),
        ],
        null,
        null,
        false,
        true,
        'no-border-bottom'
      ),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [warehouseId]
  );
}

export const movedSchema = (isRequired: boolean = false) => {
  return yup.object().shape({
    product_obtain_type_id: isRequired
      ? yup.string().required()
      : yup.string().nullable(),
    product_category_id: yup.object().shape({
      value: yup.string().required(),
    }),
    product_type_id: yup.object().shape({
      value: yup.string().required(),
    }),
    product_id: yup.object().shape({
      value: yup.string().required(),
    }),
    to_warehouse_id: yup.object().required().shape({
      value: yup.string().required(),
    }),
    amount: yup.number().positive().decimal(8, 3).required(),
    unit: yup.object().shape({
      value: yup.string().required(),
    }),
    product_warehouses: yup
      .array()
      .of(
        yup.object().shape({
          warehouse_id: yup.object().required().shape({
            value: yup.string().required(),
          }),
        })
      )
      .min(1),
  });
};
